/*
  Four-step promotional pop-up (sections/promo-steps.liquid).

  Saving. The email and the phone reach Shopify together, as one new customer,
  when the phone step is sent. Shopify's storefront form fills in a customer's
  details only when it creates them — a second sign-up with an email it already
  has changes nothing, tags included — so a phone sent after the email would
  never land on that customer. Someone who gives their email and then closes
  the phone step is still signed up: the email goes on its own at that moment.

  Both steps render a real Shopify newsletter form ({% form 'customer' %}), so
  the fields and form_type are the storefront's own. They are sent in the
  background — the page never reloads — and Shopify's answer is read from
  where it lands:

    success   Shopify redirects to ?customer_posted=true
    existing  the page comes back with neither success nor errors
    error     the page comes back with form.errors inside our form
    captcha   Shopify redirects to /challenge — the form is then submitted for
              real so the shopper can pass it, and the flow resumes on return

  Shopify's spam check (hCaptcha) is honoured, not bypassed. It wraps every
  newsletter form: on submit it fetches a token, adds it to the form and calls
  the form's submit(), which navigates. Left alone it took over this pop-up's
  forms and reloaded the page halfway through the flow. So the pop-up keeps
  the submit event to itself, asks the check to run, and points each form's
  submit() at the background sender — the token still travels with the form.
*/
(() => {
  if (customElements.get('promo-steps')) return;

  const KEY_HIDE_UNTIL = 'promo-steps:hide-until';
  const KEY_DONE = 'promo-steps:done';
  const KEY_RESUME = 'promo-steps:resume';
  const KEY_EMAIL = 'promo-steps:email';
  const DAY = 24 * 60 * 60 * 1000;
  const TAGS = 'newsletter,promo-popup';

  // U+2068/U+2069 keep a suggested address reading left to right inside the
  // Hebrew sentence around it.
  const MESSAGES = {
    email: 'נא להזין כתובת מייל תקינה',
    emailSlip: (fixed) => `נראה שיש טעות בכתובת. התכוונת ל-\u2068${fixed}\u2069?`,
    consent: 'יש לאשר את קבלת הדיוור',
    phone: 'נא להזין מספר טלפון נייד תקין',
    failed: 'משהו השתבש, נסו שוב',
    offline: 'אין חיבור לאינטרנט, נסו שוב',
  };

  const store = {
    get(k) { try { return localStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
  };
  const session = {
    get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } },
    drop(k) { try { sessionStorage.removeItem(k); } catch { /* private mode */ } },
  };

  /* ------------------------------------------------------- validation -- */

  const EMAIL = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,24}|xn--[a-z0-9-]{1,59})$/i;

  // Slips on the mailboxes people here use most. Only these exact misspellings
  // are caught, so no real address is ever turned away.
  const PROVIDER_SLIPS = {
    gmial: 'gmail', gmai: 'gmail', gamil: 'gmail', gnail: 'gmail', gmaill: 'gmail',
    gmali: 'gmail', gmil: 'gmail', gmal: 'gmail', gmsil: 'gmail',
    hotmial: 'hotmail', hotmal: 'hotmail', hotmai: 'hotmail', homail: 'hotmail', hotmil: 'hotmail',
    yaho: 'yahoo', yahho: 'yahoo', yhoo: 'yahoo',
    outlok: 'outlook', outllok: 'outlook', otlook: 'outlook',
    iclod: 'icloud', icoud: 'icloud', icloude: 'icloud',
    wala: 'walla', wallla: 'walla',
  };
  // endings that exist nowhere, and are always a slip for .com
  const COM_SLIPS = ['con', 'cmo', 'ocm', 'comm', 'coom', 'vom', 'xom', 'cpm', 'cim', 'clm'];
  // mailboxes that exist under .com only
  const ONLY_COM = ['gmail', 'icloud'];

  // What to tell the shopper about this address, or '' when it is fine.
  function emailProblem(raw) {
    const value = String(raw || '').trim();
    const at = value.lastIndexOf('@');
    if (value.length > 254 || at < 1 || at > 64 || !EMAIL.test(value)) return MESSAGES.email;

    const labels = value.slice(at + 1).toLowerCase().split('.');
    const fixed = labels.slice();
    fixed[0] = PROVIDER_SLIPS[fixed[0]] || fixed[0];
    if (COM_SLIPS.includes(fixed[fixed.length - 1])) fixed[fixed.length - 1] = 'com';
    if (ONLY_COM.includes(fixed[0])) fixed.splice(1, fixed.length, 'com');

    if (fixed.join('.') === labels.join('.')) return '';
    return MESSAGES.emailSlip(`${value.slice(0, at)}@${fixed.join('.')}`);
  }

  // 050-0000000, 052-1234567 and the like: filler, not someone's number.
  function madeUp(seven) {
    return /^(\d)\1+$/.test(seven) || '01234567890'.includes(seven) || '09876543210'.includes(seven);
  }

  // Israeli mobiles, however they are typed — 050-123-4567, 0501234567,
  // +972 50-123-4567, 00972501234567, even +972 050… — come out as
  // +972501234567. A number carrying another country's code is kept as typed.
  // Anything that cannot be a real mobile number gives null.
  function parsePhone(raw) {
    const text = String(raw || '').trim();
    if (!text || /[^\d\s()+.-]/.test(text)) return null;
    const international = /^(\+|00)/.test(text);
    let digits = text.replace(/\D/g, '');
    if (text.startsWith('00')) digits = digits.slice(2);

    if (international && !digits.startsWith('972')) {
      const plausible = digits.length >= 8 && digits.length <= 15 && !/^(\d)\1+$/.test(digits);
      return plausible ? { e164: '+' + digits } : null;
    }

    if (digits.startsWith('972')) digits = digits.slice(3);
    digits = digits.replace(/^0/, '');
    // 5, the network digit, and seven more: every Israeli mobile network
    if (!/^5\d{8}$/.test(digits) || madeUp(digits.slice(2))) return null;
    return { e164: '+972' + digits };
  }

  /* ------------------------------------------------------------ element -- */

  class PromoSteps extends HTMLElement {
    connectedCallback() {
      this.design = this.dataset.design === 'true';
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.dialog = this.querySelector('.promo-steps__dialog');
      this.emailForm = this.querySelector('#PromoSteps-Email');
      this.phoneForm = this.querySelector('#PromoSteps-Phone');
      this.current = 0;
      this.returnFocus = null;
      this.held = false;
      this.saved = false;
      this.busy = false;
      this.waiting = new Map();

      this.onKey = this.onKey.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.addEventListener('click', (e) => this.onClick(e));
      // Capture phase, so this runs before Shopify's own listeners on the form
      // and on the document, which would send the form by reloading the page.
      this.addEventListener('submit', (e) => this.onSubmit(e), true);
      this.addEventListener('input', (e) => this.onInput(e));
      this.addEventListener('change', (e) => this.onInput(e));

      // Shopify's spam check calls a form's submit() once it holds a token;
      // pointed here, the form then goes in the background. See send().
      [this.emailForm, this.phoneForm].forEach((form) => {
        if (form) form.submit = () => this.transmit(form);
      });

      if (this.design) {
        this.bindEditor();
        return;
      }

      window.addEventListener('pagehide', this.onPageHide);

      // Back from Shopify's captcha page: the customer is in, so pick up where
      // the shopper left off.
      const resume = Number(session.get(KEY_RESUME));
      if (resume && /customer_posted=true/.test(location.search)) {
        session.drop(KEY_RESUME);
        this.email = session.get(KEY_EMAIL) || '';
        this.markSaved();
        this.open(resume);
        return;
      }

      // Someone arriving on a link from your own email or SMS is on your list
      // already, so this device is marked as theirs and the pop-up leaves them
      // alone from here on. It is how the marketing tools recognise a known
      // subscriber too: by what their browser remembers from a sign-up or a
      // campaign click — never by knowing who a new visitor is.
      if (this.knownFromCampaign()) store.set(KEY_DONE, '1');

      if (this.suppressed()) return;
      this.warm(1);
      const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.timer = setTimeout(() => this.open(1), delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('pagehide', this.onPageHide);
      document.documentElement.classList.remove('promo-steps-lock');
      (this.editorListeners || []).forEach(([type, fn]) => document.removeEventListener(type, fn));
    }

    suppressed() {
      if (store.get(KEY_DONE)) return true;
      const until = Number(store.get(KEY_HIDE_UNTIL));
      return Number.isFinite(until) && until > Date.now();
    }

    // Campaign links carry where they came from: Flashy sends
    // utm_source=flashyapp, Shopify Email sends utm_source=shopify_email, and
    // most tools mark email or SMS in utm_medium.
    knownFromCampaign() {
      if (this.dataset.knownFromCampaign !== 'true') return false;
      const params = new URLSearchParams(location.search);
      if (/mail|sms/.test((params.get('utm_medium') || '').toLowerCase())) return true;
      const source = (params.get('utm_source') || '').toLowerCase();
      if (!source) return false;
      return (this.dataset.knownSources || '')
        .toLowerCase()
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)
        .some((name) => source.includes(name));
    }

    // In the theme editor the pop-up opens only while its own section is
    // selected in the sidebar, on the step chosen for preview, and closes when
    // another section is picked — otherwise it would sit over every page being
    // edited. Nothing is saved and no dismissal is remembered here.
    bindEditor() {
      const id = this.dataset.sectionId;
      const mine = (e) => e.detail && e.detail.sectionId === id;
      const show = (e) => { if (mine(e)) this.open(Number(this.dataset.previewStep) || 1); };
      const hide = (e) => { if (mine(e)) this.close(); };
      this.editorListeners = [
        ['shopify:section:select', show],
        ['shopify:section:load', show],
        ['shopify:section:deselect', hide],
      ];
      this.editorListeners.forEach(([type, fn]) => document.addEventListener(type, fn));
    }

    /* ----------------------------------------------------------- flow -- */

    open(step) {
      if (!this.held) {
        this.held = true;
        this.returnFocus = document.activeElement;
        this.scrollY = window.scrollY;
        this.setAttribute('open', '');
        document.documentElement.classList.add('promo-steps-lock');
        document.addEventListener('keydown', this.onKey);
        // The pop-up appears over the page; it never moves it. Locking the page
        // can nudge it by itself as the scrollbar goes, and in the theme editor
        // selecting this section pulls the preview towards it — so the position
        // is held for a moment, until both have had their say.
        this.holdScroll();
      }
      this.show(step);
    }

    holdScroll() {
      const until = Date.now() + 900;
      const keep = () => {
        // a pop-up that has been closed or taken off the page holds nothing
        if (!this.held || !this.isConnected) return;
        this.restoreScroll();
        if (Date.now() < until) requestAnimationFrame(keep);
      };
      requestAnimationFrame(keep);
    }

    // Back to where the page was, in one jump. The theme scrolls smoothly by
    // default, which would glide the page across in full view — and asking for
    // that in style alone is not enough: with other style changes pending (the
    // lock going on or off, for one) the old smooth value is what the browser
    // reads. Saying it outright works, and where 'instant' is not understood
    // the style is set and given a moment to take.
    restoreScroll() {
      if (Math.abs(window.scrollY - this.scrollY) <= 1) return;
      try {
        window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
        return;
      } catch {
        /* older browsers do not know 'instant' */
      }
      const root = document.documentElement;
      const behaviour = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      void root.offsetHeight;
      window.scrollTo(0, this.scrollY);
      requestAnimationFrame(() => { root.style.scrollBehavior = behaviour; });
    }

    show(step) {
      if (!this.stepEl(step)) return;
      this.current = step;
      this.steps.forEach((el) => { el.hidden = Number(el.dataset.step) !== step; });
      this.warm(step + 1);
      if (step === 3) {
        this.carryEmail();
        this.armCaptcha(this.phoneForm);
      }
      this.dialog.scrollTop = 0;
      // Focus goes to the dialog itself: keyboard and screen-reader users are
      // inside it and one Tab from the first control, while nobody gets a focus
      // ring on a button they never touched, or a phone keyboard springing up
      // over the email step. Not in the editor, where it would pull focus out
      // of the settings being typed into.
      if (this.design) return;
      this.dialog.focus({ preventScroll: true });
    }

    close() {
      clearTimeout(this.timer);
      const wasOpen = this.held;
      this.held = false;
      this.removeAttribute('open');
      document.documentElement.classList.remove('promo-steps-lock');
      document.removeEventListener('keydown', this.onKey);

      if (!this.design) {
        // However it is closed, it stays away for the chosen number of days.
        const days = Math.max(1, Number(this.dataset.days) || 30);
        store.set(KEY_HIDE_UNTIL, String(Date.now() + days * DAY));
        // Gave the email, then shut the phone step: still a sign-up.
        if (this.current === 3 && this.email && !this.saved && !this.busy) this.saveEmailOnly();
      }

      // The page is left exactly where it was, even on phones that scroll it
      // while their keyboard is up.
      if (wasOpen) this.restoreScroll();

      if (this.returnFocus && typeof this.returnFocus.focus === 'function') {
        this.returnFocus.focus({ preventScroll: true });
      }
    }

    finish() {
      if (!this.design) store.set(KEY_DONE, '1');
      const target = this.dataset.target;
      if (target && !this.design) {
        this.leaving = true;
        window.location.href = target;
        return;
      }
      this.close();
    }

    stepEl(step) {
      return this.steps.find((el) => Number(el.dataset.step) === step);
    }

    // Pictures are lazy, so a step's images would only start downloading once
    // it is shown. Asking for the next step's early means it is ready by then.
    warm(step) {
      const el = this.stepEl(step);
      if (!el) return;
      el.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    /* -------------------------------------------------------- events -- */

    onClick(e) {
      if (e.target.closest('[data-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.target.closest('[data-next]')) {
        e.preventDefault();
        this.show(this.current + 1);
        return;
      }
      if (e.target.closest('[data-finish]')) {
        e.preventDefault();
        this.finish();
      }
    }

    onKey(e) {
      if (!this.hasAttribute('open')) return;
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    trapFocus(e) {
      const focusable = Array.from(this.stepEl(this.current).querySelectorAll(
        'button:not([disabled]), input:not([type="hidden"]):not([disabled]), a[href]'
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // focus starts on the dialog itself, so Shift+Tab from there has to wrap
      // to the end rather than fall out onto the page behind
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Step 2's button only wakes up once there is an email and the consent box
    // is ticked. Whatever is changed, its error goes away until the next try.
    onInput(e) {
      const form = e.target.closest('form');
      if (!form) return;
      if (form === this.emailForm) this.refreshEmailButton(form);
      e.target.removeAttribute('aria-invalid');
      this.setError(form, '');
    }

    refreshEmailButton(form) {
      const email = form.querySelector('input[type="email"]');
      const consent = form.querySelector('[data-consent]');
      const button = form.querySelector('button[type="submit"]');
      button.disabled = !(email.value.trim() && consent.checked);
    }

    setError(form, message) {
      const box = form.querySelector('[data-client-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
    }

    fail(form, field, message) {
      field.setAttribute('aria-invalid', 'true');
      this.setError(form, message);
      field.focus();
    }

    /* ------------------------------------------------------ the steps -- */

    onSubmit(e) {
      const form = e.target;
      if (form !== this.emailForm && form !== this.phoneForm) return;
      // This submission is the pop-up's alone: not the browser's, and not
      // Shopify's scripts', which would send it by reloading the page.
      e.preventDefault();
      e.stopPropagation();
      if (form === this.emailForm) this.submitEmail(form);
      else this.submitPhone(form);
    }

    submitEmail(form) {
      const input = form.querySelector('input[type="email"]');
      const consent = form.querySelector('[data-consent]');
      const problem = emailProblem(input.value);
      if (problem) {
        this.fail(form, input, problem);
        return;
      }
      if (!consent.checked) {
        this.fail(form, consent, MESSAGES.consent);
        return;
      }
      this.email = input.value.trim();
      session.set(KEY_EMAIL, this.email);
      // Nothing is sent yet: the email goes with the phone, as one customer.
      this.show(3);
    }

    async submitPhone(form) {
      if (this.busy) return;
      const input = form.querySelector('input[type="tel"]');
      const button = form.querySelector('button[type="submit"]');
      const phone = parsePhone(input.value);
      if (!phone) {
        this.fail(form, input, MESSAGES.phone);
        return;
      }
      if (this.design) {
        this.show(4);
        return;
      }

      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      const result = await this.saveCustomer(phone);
      this.busy = false;
      button.removeAttribute('aria-busy');
      if (!this.held || result === 'challenge') return;

      if (result === 'ok') {
        this.show(4);
      } else if (result.field === 'email') {
        // Shopify turned the email down after all: back to where it is typed.
        this.show(2);
        this.fail(this.emailForm, this.emailForm.querySelector('input[type="email"]'), MESSAGES.email);
      } else {
        this.setError(form, result.error || MESSAGES.failed);
      }
    }

    carryEmail() {
      const carried = this.querySelector('[data-carried-email]');
      if (carried) carried.value = this.email || session.get(KEY_EMAIL) || '';
    }

    /* ------------------------------------------------------- saving -- */

    // One new customer carrying both the email and the phone.
    async saveCustomer(phone) {
      const form = this.phoneForm;
      const tel = form.querySelector('input[type="tel"]');
      const typed = tel.value;
      this.carryEmail();
      form.querySelector('[data-tags]').value = TAGS;
      tel.value = phone.e164;

      const result = await this.send(form);
      if (result === 'challenge') return this.passChallenge(form, 4);
      tel.value = typed;
      if (result === 'ok') {
        this.markSaved();
        return 'ok';
      }
      if (result.field !== 'phone') return result;

      // Shopify refused the number itself — usually because another customer
      // already has it. The email must still get in: it goes alone, and the
      // number rides along as a tag on the new customer.
      return this.saveEmailOnly(phone, true);
    }

    // The email alone, as a new customer: for someone who closed the phone
    // step, or whose number Shopify would not take.
    async saveEmailOnly(phone, interactive = false) {
      const form = this.emailForm;
      form.querySelector('input[type="email"]').value = this.email;
      form.querySelector('[data-tags]').value = phone
        ? `${TAGS},phone-${phone.e164.replace(/\D/g, '')}`
        : TAGS;

      const result = await this.send(form);
      if (result === 'ok') this.markSaved();
      // Shopify's captcha page is offered only while the shopper is still in
      // the pop-up; after a close it would pull them off the page.
      if (result === 'challenge' && interactive) return this.passChallenge(form, 4);
      return result;
    }

    // The customer is in Shopify: from now on this visitor is a subscriber and
    // is never asked to sign up again.
    markSaved() {
      this.saved = true;
      store.set(KEY_DONE, '1');
    }

    // Starts Shopify's spam check on a form ahead of time, so nobody waits for
    // it to load when they press the button.
    armCaptcha(form) {
      const captcha = window.Shopify && window.Shopify.captcha;
      if (this.design || !form || form.dataset.cptcha) return;
      if (captcha && typeof captcha.protect === 'function') captcha.protect(form, () => {});
    }

    // Sends one of the pop-up's forms without leaving the page, and resolves
    // with Shopify's answer: 'ok', 'challenge' or { error, field }.
    //
    // Where the store runs Shopify's spam check, the form goes through it: the
    // check fetches a token, puts it in the form and calls form.submit(), which
    // connectedCallback pointed at transmit(). A check that never loads —
    // blocked, or offline — must not strand the shopper, so after a while the
    // form goes without it, and Shopify may then ask for its captcha page.
    send(form) {
      return new Promise((resolve) => {
        const timers = [];
        const finish = (result) => {
          timers.forEach(clearTimeout);
          this.waiting.delete(form);
          resolve(result);
        };
        this.waiting.set(form, finish);

        const captcha = window.Shopify && window.Shopify.captcha;
        if (form.hasAttribute('data-hcaptcha-bound')) {
          form.submit();
        } else if (captcha && typeof captcha.protect === 'function') {
          captcha.protect(form, () => form.submit());
        } else {
          this.transmit(form);
          return;
        }
        timers.push(setTimeout(() => {
          if (!form.hasAttribute('data-hcaptcha-bound')) this.transmit(form);
        }, 8000));
        timers.push(setTimeout(() => finish({ error: MESSAGES.failed }), 30000));
      });
    }

    // What form.submit() runs. Posts the form in the background and hands
    // Shopify's answer to whoever is waiting for it.
    async transmit(form) {
      const result = await this.post(form);
      const finish = this.waiting.get(form);
      if (finish) finish(result);
    }

    async post(form) {
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (/\/challenge/.test(res.url)) return 'challenge';
        if (/customer_posted=true/.test(res.url)) return 'ok';
        // No redirect: Shopify re-rendered the page — with our form's errors
        // when it refused something, and with neither when the email already
        // belongs to a customer, who is on the list either way.
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const server = doc.querySelector(`#${form.id} [data-server-error]`)
          || doc.querySelector('.promo-steps [data-server-error]');
        if (server) return { error: MESSAGES.failed, field: server.dataset.serverError || 'other' };
        return res.ok ? 'ok' : { error: MESSAGES.failed };
      } catch {
        return { error: MESSAGES.offline };
      }
    }

    // Shopify wants its captcha page: the form is submitted for real so the
    // shopper can pass it, and the flow resumes on the step after.
    passChallenge(form, next) {
      this.leaving = true;
      session.set(KEY_RESUME, String(next));
      if (this.email) session.set(KEY_EMAIL, this.email);
      HTMLFormElement.prototype.submit.call(form);
      return 'challenge';
    }

    // Leaving the page with the phone step still open: the email is sent on
    // its own as the page goes, rather than lost with it. There is no time for
    // the spam check here, so Shopify may turn it away — a best effort on top
    // of the close button, which is the usual way out.
    onPageHide() {
      if (!this.held || this.current !== 3 || !this.email || this.saved || this.busy || this.leaving) return;
      if (!navigator.sendBeacon) return;
      const form = this.emailForm;
      form.querySelector('input[type="email"]').value = this.email;
      form.querySelector('[data-tags]').value = TAGS;
      navigator.sendBeacon(form.action, new FormData(form));
    }
  }

  customElements.define('promo-steps', PromoSteps);
})();
